import { observer } from 'mobx-react';
import { Fragment, useState, useEffect } from 'react';
import { CreativeSelectionModes } from '../../../enums/creative-selection-modes';
import { useCampaignInputOptions } from '../../../hooks/apis/use-campaign-input-options';
import { useStores } from '@monorepo/controlled/src/hooks/use-stores';
import { CampaignModel, ICampaign } from '../../../models/campaign.model';
import { StrategyModel } from '../../../models/strategy.model';
import { TestGroupModel } from '../../../models/test-group.model';
import { SecondaryButton } from '@monorepo/base/src/components/buttons/buttons';
import { ButtonGroup } from '@monorepo/base/src/components/buttons/group/button-group';
import { GButton } from '@monorepo/base/src/components/buttons/group/button/g-button';
import { Card } from '@monorepo/base/src/components/card/card';
import { Dropdown, option } from '@monorepo/base/src/components/dropdown/dropdown';
import { Input } from '@monorepo/base/src/components/form/input/input';
import { Textarea } from '@monorepo/base/src/components/form/textarea/textarea';
import { Toggle } from '@monorepo/base/src/components/form/toggle/toggle';
import { Spacer } from '@monorepo/base/src/components/spacer/spacer';
import styles from './test-form.module.scss';
import { IAdminxStore } from '../../../stores';
import { Statuses } from '../../../enums/status';
import { ScorePicker } from './components';
import { DataAttribute } from '@monorepo/tools/src/lib/models/data-attr.model';
import { IPerformanceOptionsSortBy, PerformancesColumnsSnakeCase, useReportsList } from '../../../hooks/apis/use-reports-list';
import { CampaignsApi } from '../../../apis/campaigns.api';

export const TestForm = observer(() => {
	const { testStore, campaignStore } = useStores<IAdminxStore>();
	const inputOptions = campaignStore.getInputOptions();
	const campaignCrudStore = campaignStore.getCrud();
	const testCrudStore = testStore.getCrud();
	const formStore = testCrudStore.getFormStore();
	const test = testCrudStore.getData();
	const testGroups: undefined | TestGroupModel[] = test.getTestGroups();
	const campaignListStore = campaignStore.getListStore();
	const campaigns = campaignListStore
		.getData()
		?.getData()
		.map(campaign => new CampaignModel(campaign as ICampaign));
	const campaignOptions = campaigns?.map(campaign => campaign.getName() + ' (' + campaign.getId() + ')' || '');
	const [isRemoved, setIndexToRemove] = useState<number | undefined>(undefined);
	const campaign = test.getCampaign();
	const strategies = inputOptions.getStrategies();
	const campaignsCSM = campaign?.getCreativeSelectionMode();
	const [campaignLoading, setCampaignLoading] = useState(false);
	const [currentPhases, setCurrentPhases] = useState(campaignCrudStore.getData().getPhases());
	const isCampaignsLoading = campaignListStore.getIsLoading();
	const normalizeGroupName = (name?: string) => name?.trim().toUpperCase();
	const activeGroupEntries = (testGroups || [])
		.map((group, index) => ({ group, index }))
		.filter(({ group }) => group.getStatus() === Statuses.Active);
	const activeAllocationGroupEntries = activeGroupEntries.filter(({ group }) => normalizeGroupName(group.getName()) !== 'A');
	const totalActiveGroupsWeight = activeGroupEntries.reduce((acc, { group }) => acc + (group.getWeight() || 0), 0);
	const maxAllocationPercentage = Math.max(0, Math.min(100, test.getPercentage() || 0));
	const [desiredWeightPercentages, setDesiredWeightPercentages] = useState<Record<number, string>>({});
	const [isWeightAllocationApplied, setIsWeightAllocationApplied] = useState(false);

	useCampaignInputOptions(['strategies', 'keyGenerators', 'designs']);
	useReportsList(campaignListStore, {
		groupBys: [PerformancesColumnsSnakeCase.Id, PerformancesColumnsSnakeCase.Name],
		reportName: 'adxCampaigns',
		sortBy: IPerformanceOptionsSortBy.Id,
	});

	const onCampaignSelect = (options: Set<option>) => {
		const optionsArr = Array.from(options);
		const selectedCampaign = optionsArr.map(option => {
			return campaigns?.find(
				campaign =>
					campaign && (option === campaign.getName() + ' (' + campaign.getId() + ')' || option === campaign.getId()?.toString())
			);
		});
		if (selectedCampaign[0]) {
			test.setCampaign(selectedCampaign[0]);
			test.setCampaignPhaseId(undefined);
		}
	};

	const onPhaseSelect = (options: Set<option> | undefined) => {
		if (!options || options.size === 0) {
			test.setCampaignPhaseId(null);
			campaign?.setPhase(undefined);
			return;
		}

		const optionsArr = Array.from(options);
		const selectedPhase = optionsArr.map(option => currentPhases?.find(phase => option === phase.phase.name));
		if (selectedPhase[0]) {
			test.setCampaignPhaseId(selectedPhase[0].id);
			campaign?.setPhase(selectedPhase[0]);
		}
	};

	const onStrategySelect = (testGroup: TestGroupModel, strategyName: option | undefined) => {
		if (!strategyName) {
			testGroup.setStrategy(undefined);
			return;
		}
		const strategy = strategies.find(s => `${s.name} (${s.id})` === strategyName);

		if (strategy) {
			testGroup.setStrategy(new StrategyModel(strategy));
		}
	};

	const removeTestGroupAnimation = (index: number) => {
		setIndexToRemove(index);
		setTimeout(() => {
			test.removeTestGroup(index);
			setIndexToRemove(undefined);
		}, 200);
	};

	useEffect(() => {
		if (campaign?.id) {
			setCampaignLoading(true);
			CampaignsApi.get(campaign.id)
				.then(res => {
					const data = new CampaignModel(res);
					setCurrentPhases(data?.getPhases() || []);
					setCampaignLoading(false);
				})
				.catch(() => setCampaignLoading(false));
		}
	}, [campaign?.id]);

	useEffect(() => {
		if (!activeAllocationGroupEntries.length) {
			setDesiredWeightPercentages({});
			return;
		}

		const defaultPercentages = activeAllocationGroupEntries.reduce(
			(acc, { group, index }) => {
				const weight = group.getWeight() || 0;
				acc[index] = totalActiveGroupsWeight > 0 ? ((weight / totalActiveGroupsWeight) * maxAllocationPercentage).toFixed(1) : '';
				return acc;
			},
			{} as Record<number, string>
		);
		setDesiredWeightPercentages(defaultPercentages);
	}, [activeAllocationGroupEntries.length, totalActiveGroupsWeight, maxAllocationPercentage]);

	const applyDesiredWeightPercentages = () => {
		if (!activeAllocationGroupEntries.length) {
			return;
		}

		const parsedPercentages = activeAllocationGroupEntries.map(({ index }) => {
			const rawValue = desiredWeightPercentages[index];
			const value = Number(rawValue);
			return Number.isFinite(value) && value >= 0 ? value : 0;
		});

		const percentagesSum = parsedPercentages.reduce((acc, value) => acc + value, 0);
		if (percentagesSum - maxAllocationPercentage > 0.001) {
			return;
		}
		activeAllocationGroupEntries.forEach(({ group }, index) => {
			const nextWeight = Math.max(1, Math.round(parsedPercentages[index]));
			group.setWeight(nextWeight);
		});
		setIsWeightAllocationApplied(true);
		setTimeout(() => {
			setIsWeightAllocationApplied(false);
		}, 1000);
	};
	const desiredPercentagesSum = activeAllocationGroupEntries.reduce((acc, { index }) => {
		const value = Number(desiredWeightPercentages[index]);
		return acc + (Number.isFinite(value) && value >= 0 ? value : 0);
	}, 0);
	const isDesiredPercentagesSumValid = activeAllocationGroupEntries.length
		? desiredPercentagesSum - maxAllocationPercentage <= 0.001 && desiredPercentagesSum - maxAllocationPercentage >= 0
		: false;

	return (
		<div className={styles.wrapper}>
			<Card title={'Test Information'} icon={'paint'} debugProps={{ dataAttrs: [new DataAttribute('id', 'test_form_main')] }}>
				<Fragment>
					<Input
						value={test.getName()}
						placeholder={'Test Name'}
						onValue={value => {
							test.setName(value);
							formStore.clearError('name');
						}}
						required={true}
						error={formStore.getErrors().get('name')?.getMsg()}
						debugProps={{ dataAttrs: [new DataAttribute('id', 'test_name')] }}
					/>
					<Dropdown
						label={'Select Campaign'}
						options={campaignOptions || []}
						defaultOption={campaign?.name}
						onSelect={(campaign: Set<option> | undefined) => campaign && onCampaignSelect(campaign)}
						autocomplete={true}
						placeholder={isCampaignsLoading ? 'Loading campaigns...' : 'Search Campaign'}
						disabled={isCampaignsLoading}
						isLoading={isCampaignsLoading}
						required={true}
						error={formStore.getErrors().get('campaign')?.getMsg()}
						debugProps={{ dataAttrs: [new DataAttribute('id', 'test_campaign')] }}
					/>
					<Dropdown
						label={'Select Campaign Phase'}
						defaultOption={
							test.getCampaignPhaseId()
								? currentPhases.find(phase => phase.id === test.getCampaignPhaseId())?.phase.name
								: undefined
						}
						options={currentPhases.map(p => p.phase.name) || []}
						onSelect={(phases: Set<option> | undefined) => onPhaseSelect(phases)}
						disabled={!campaign || campaignLoading}
						required={false}
					/>
					<Textarea
						value={test.getDescription()}
						placeholder={'Description'}
						onValue={value => {
							test.setDescription(value);
							formStore.clearError('description');
						}}
						error={formStore.getErrors().get('description')?.getMsg()}
						debugProps={{ dataAttrs: [new DataAttribute('id', 'test_description')] }}
					/>
					<Input
						type={'number'}
						value={test?.getPercentage()?.toString()}
						placeholder={'Percentage'}
						onValue={value => {
							test.setPercentage(parseInt(value));
							formStore.clearError('name');
						}}
						required={true}
						error={formStore.getErrors().get('percentage')?.getMsg()}
						debugProps={{ dataAttrs: [new DataAttribute('id', 'test_percentage')] }}
					/>
					<div className={`${styles.testGroupWrapper} ${styles.normalGroup}`}>
						<div className={`${styles.testTitleWrapper}`}>
							<span className={styles.title}>Group Normal</span>
						</div>
						<Dropdown
							label={'Select Cache & bid Key Generator'}
							defaultOption={campaign?.getCacheKeyGenerator()}
							options={campaignStore.getInputOptions().getKeyGenerators()}
							error={formStore.getErrors().get('cache_key_generator')?.getMsg()}
							disabled={true}
							onSelect={(cacheKeyGenerator: Set<option> | undefined) => {
								campaign?.setCacheKeyGenerator(cacheKeyGenerator?.values().next().value);
								formStore.clearError('cache_key_generator');
							}}
							debugProps={{ dataAttrs: [new DataAttribute('id', 'test_cache_key_generator')] }}
						/>
						<Dropdown
							required={true}
							defaultOption={campaign?.getStrategy()?.name}
							label={'Select Strategy'}
							disabled={true}
							error={formStore.getErrors().get('strategy')?.getMsg()}
							options={[]}
							onSelect={() => null}
							debugProps={{ dataAttrs: [new DataAttribute('id', 'test_strategy')] }}
						/>
						<Spacer />
						{/* todo-score-picker */}
						{/*<ScorePicker*/}
						{/*	inputOptions={inputOptions}*/}
						{/*	design={campaign?.design_score_picker}*/}
						{/*	format={campaign?.format_score_picker}*/}
						{/*	setDesign={(v) => {*/}
						{/*		if (campaign) campaign.design_score_picker = v;*/}
						{/*	}}*/}
						{/*	setFormat={(v) => {*/}
						{/*		if (campaign) campaign.format_score_picker = v;*/}
						{/*	}}*/}
						{/*/>*/}
						<Spacer />
						<ButtonGroup
							title={'Creative Selection Modes'}
							defaultValues={campaignsCSM ? new Set([campaignsCSM]) : undefined}
							error={formStore.getErrors().get('creative_selection_mode')?.getMsg()}
							onChange={setValues => {
								campaign?.setCreativeSelectionMode(setValues.values().next().value);
								formStore.clearError('creative_selection_mode');
							}}>
							<GButton
								disabled={true}
								icon={'slash-circle-01'}
								text={CreativeSelectionModes.Off}
								debugProps={{ dataAttrs: [new DataAttribute('id', 'test_selection_mode_off')] }}
								value={CreativeSelectionModes.Off}
							/>
							<GButton
								disabled={true}
								icon={'edit-05'}
								text={CreativeSelectionModes.Format}
								value={CreativeSelectionModes.Format}
								debugProps={{ dataAttrs: [new DataAttribute('id', 'test_selection_mode_format')] }}
							/>
							<GButton
								disabled={true}
								icon={'design-services'}
								text={CreativeSelectionModes.Design}
								value={CreativeSelectionModes.Design}
								debugProps={{ dataAttrs: [new DataAttribute('id', 'test_selection_mode_design')] }}
							/>
							<GButton
								disabled={true}
								icon={'circle'}
								text={CreativeSelectionModes.Full}
								value={CreativeSelectionModes.Full}
								debugProps={{ dataAttrs: [new DataAttribute('id', 'test_selection_mode_full')] }}
							/>
						</ButtonGroup>
						<Textarea
							value={campaign?.getDestinationUrl()}
							placeholder={'Destination URL'}
							onValue={value => {
								campaign?.setDestinationUrl(value);
								formStore.clearError('dest_url');
							}}
							error={formStore.getErrors().get('dest_url')?.getMsg()}
							disabled={true}
							debugProps={{ dataAttrs: [new DataAttribute('id', 'test_dest_url')] }}
						/>
					</div>
				</Fragment>
			</Card>
			{testGroups?.map((testGroup, i) => {
				const strategy = testGroup.getStrategy();
				const creativeSelectionMode = testGroup.getCreativeSelectionMode();
				const weight = testGroup.getWeight() || 0;
				const isActiveGroup = testGroup.getStatus() === Statuses.Active;
				const isControlGroup = normalizeGroupName(testGroup.getName()) === 'A';
				const controlGroupPercentage = 100 - maxAllocationPercentage;
				const experimentTotalWeight = activeAllocationGroupEntries.reduce((acc, { group }) => acc + (group.getWeight() || 0), 0);
				let weightPercentage = 0;
				if (isActiveGroup) {
					if (isControlGroup) {
						weightPercentage = controlGroupPercentage;
					} else if (experimentTotalWeight > 0) {
						weightPercentage = (weight / experimentTotalWeight) * maxAllocationPercentage;
					}
				}

				const groupNumber = i + 1;
				return (
					<Card
						className={`${styles.testGroupWrapper} ${isRemoved === i ? styles.removeAnimation : ''}`}
						key={i}
						debugProps={{ dataAttrs: [new DataAttribute('id', `test_group_${groupNumber}_form`)] }}>
						<div className={`${styles.testTitleWrapper}`}>
							<span className={styles.title}>Test Group {i + 1}</span>
							<Toggle
								classNames={styles.status}
								label={testGroup.getStatus() === Statuses.Active ? Statuses.Active : Statuses.Paused}
								onChange={status => testGroup.setStatus(status ? Statuses.Active : Statuses.Paused)}
								defaultValue={testGroup.getStatus() === Statuses.Active}
								debugProps={{ dataAttrs: [new DataAttribute('id', `test_group_${groupNumber}_status`)] }}
							/>
							{testGroups.length > 1 ? (
								<SecondaryButton
									className={styles.closeBtn}
									onClick={() => removeTestGroupAnimation(i)}
									isRotateIcon={false}
									icon={'x-close'}
									debugProps={{ dataAttrs: [new DataAttribute('id', `test_group_${groupNumber}_close_button`)] }}
								/>
							) : null}
						</div>
						<Input
							value={testGroup.getName()}
							placeholder={'Group Name'}
							onValue={value => {
								testGroup.setName(value);
								formStore.clearError('name');
							}}
							required={true}
							error={formStore.getErrors().get('name')?.getMsg()}
							debugProps={{ dataAttrs: [new DataAttribute('id', `test_group_${groupNumber}_name`)] }}
						/>
						<Input
							value={testGroup.getCreativeGroup()}
							placeholder={'Creative Group'}
							onValue={value => {
								testGroup.setCreativeGroup(value);
								formStore.clearError('creative_group');
							}}
							error={formStore.getErrors().get('creative_group')?.getMsg()}
							debugProps={{ dataAttrs: [new DataAttribute('id', `test_creative_group_${groupNumber}`)] }}
						/>
						<Dropdown
							label={'Select Cache & bid Key Generator'}
							defaultOption={testGroup.getCacheKeyGenerator()}
							options={campaignStore.getInputOptions().getKeyGenerators()}
							onSelect={(cacheKeyGenerator: Set<option> | undefined) => {
								testGroup.setCacheKeyGenerator(cacheKeyGenerator?.values().next().value);
								formStore.clearError('cache_key_generator');
							}}
							error={formStore.getErrors().get('cache_key_generator')?.getMsg()}
							debugProps={{ dataAttrs: [new DataAttribute('id', `test_cache_key_generator_${groupNumber}`)] }}
						/>
						<Dropdown
							defaultOption={strategy ? `${strategy?.name} (${strategy?.id})` : ''}
							label={'Select Strategy'}
							options={strategies.map(s => `${s.name} (${s.id})`)}
							onSelect={(strategyName: Set<option> | undefined) => {
								onStrategySelect(testGroup, strategyName?.values().next().value);
							}}
							autocomplete={true}
							error={formStore.getErrors().get('strategy')?.getMsg()}
							debugProps={{ dataAttrs: [new DataAttribute('id', `test_group_${groupNumber}_strategy`)] }}
						/>
						<Spacer />
						<ScorePicker
							inputOptions={inputOptions}
							design={testGroup.design_score_picker}
							format={testGroup.format_score_picker}
							setDesign={v => (testGroup.design_score_picker = v)}
							setFormat={v => (testGroup.format_score_picker = v)}
						/>
						<Input
							type={'number'}
							value={testGroup?.getWeight()?.toString()}
							placeholder={`Weight (${weightPercentage.toFixed(1)}%)`}
							onValue={value => testGroup?.setWeight(parseInt(value))}
							min={1}
							debugProps={{ dataAttrs: [new DataAttribute('id', `test_group_${groupNumber}_weight`)] }}
						/>
						<Input
							type={'number'}
							value={testGroup?.getSecondBidFactor()?.toString()}
							placeholder={'Second Bid Factor'}
							onValue={value => testGroup?.setSecondBidFactor(parseFloat(value))}
							min={1}
							debugProps={{ dataAttrs: [new DataAttribute('id', `test_group_${groupNumber}_second_bid_factor`)] }}
						/>
						<Spacer />
						<ButtonGroup
							title={'Creative Selection Modes'}
							defaultValues={creativeSelectionMode ? new Set([creativeSelectionMode]) : undefined}
							error={formStore.getErrors().get('creative_selection_mode')?.getMsg()}
							onChange={setValues => {
								testGroup.setCreativeSelectionMode(setValues.values().next().value);
								formStore.clearError('creative_selection_mode');
							}}>
							<GButton
								icon={'slash-circle-01'}
								text={CreativeSelectionModes.Off}
								value={CreativeSelectionModes.Off}
								debugProps={{
									dataAttrs: [new DataAttribute('id', `test_group_${groupNumber}_creative_selection_mode_off_button`)],
								}}
							/>
							<GButton
								icon={'edit-05'}
								text={CreativeSelectionModes.Format}
								value={CreativeSelectionModes.Format}
								debugProps={{
									dataAttrs: [new DataAttribute('id', `test_group_${groupNumber}_creative_selection_mode_format_button`)],
								}}
							/>
							<GButton
								icon={'design-services'}
								text={CreativeSelectionModes.Design}
								value={CreativeSelectionModes.Design}
								debugProps={{
									dataAttrs: [new DataAttribute('id', `test_group_${groupNumber}_creative_selection_mode_design_button`)],
								}}
							/>
							<GButton
								icon={'circle'}
								text={CreativeSelectionModes.Full}
								value={CreativeSelectionModes.Full}
								debugProps={{
									dataAttrs: [new DataAttribute('id', `test_group_${groupNumber}_creative_selection_mode_full_button`)],
								}}
							/>
						</ButtonGroup>
						<Textarea
							value={testGroup.getDestinationUrl()}
							placeholder={'Destination URL'}
							onValue={value => {
								testGroup.setDestinationUrl(value);
								formStore.clearError('dest_url');
							}}
							error={formStore.getErrors().get('dest_url')?.getMsg()}
							rows={7}
							debugProps={{ dataAttrs: [new DataAttribute('id', `test_group_${groupNumber}_dest_url_input`)] }}
						/>
					</Card>
				);
			})}
			{activeAllocationGroupEntries.length ? (
				<Card title={'Test Groups Weight Distribution'} collapsible>
					{activeAllocationGroupEntries.map(({ group: testGroup, index: i }) => {
						const groupLabel = testGroup.getName() || `Test Group ${i + 1}`;
						return (
							<div key={`test_group_${i}_weight_percentage`} className={styles.weightAllocationGroup}>
								<div className={styles.weightAllocationGroupLabel}>{`Group Name: ${groupLabel}`}</div>
								<Input
									type={'number'}
									value={desiredWeightPercentages[i] || ''}
									title={`${groupLabel} - Desired Weight %`}
									placeholder={'Weight %'}
									onValue={value => {
										setDesiredWeightPercentages(prev => ({
											...prev,
											[i]: value,
										}));
									}}
									min={0}
									max={maxAllocationPercentage}
									debugProps={{ dataAttrs: [new DataAttribute('id', `test_group_${i + 1}_desired_weight_percentage`)] }}
								/>
							</div>
						);
					})}
					<div className={styles.weightAllocationNote}>
						{isDesiredPercentagesSumValid
							? `Percentages are within the test cap (${maxAllocationPercentage.toFixed(1)}%).`
							: `Percentages must be up to ${maxAllocationPercentage.toFixed(1)}%. Current total: ${desiredPercentagesSum.toFixed(1)}%.`}
					</div>
					<div className={styles.weightAllocationButtonWrapper}>
						<SecondaryButton
							className={`${styles.weightAllocationButton} ${isWeightAllocationApplied ? styles.weightAllocationButtonApplied : ''}`}
							disabled={!isDesiredPercentagesSumValid}
							onClick={applyDesiredWeightPercentages}
							debugProps={{ dataAttrs: [new DataAttribute('id', 'apply_weight_percentages_button')] }}>
							Apply Percentages To Weights
						</SecondaryButton>
					</div>
				</Card>
			) : null}
		</div>
	);
});
